// Libraries
import React, { useState, useRef, useCallback } from 'react';

import { Box } from '@chakra-ui/react';
// Components
import CircularProgress from '@material-ui/core/CircularProgress';
import CharacterCount from '@tiptap/extension-character-count';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
// Hooks
import useOnClickOutside from 'components/hooks/useOnClickOutside';
import sanitizeHtml from 'sanitize-html';

// Theme
import theme from 'constants/theme';

// Typography
import { Typography12 } from 'uiToolkit/Typography';

// Styles
import './RichTextEditor.css';
import RTEMenuBar from './components/RTEMenuBar';

const EMPTY_EDITOR_CHAR_COUNT = 4;

function RichTextEditor({
  placeholder = '',
  containerWidth = '100%',
  disabled = false,
  readOnly = false,
  maxCharLimit = null,
  onBlur,
  loading = false,
  initialText = '',
  plainText = '',
  showError = false,
  showToolbar = true,
}) {
  const [charLimitError, setCharLimitError] = useState(false);

  // By default in nodeSize mode char count extension show 4 chars in empty state
  // so this is just to offset that
  maxCharLimit = maxCharLimit ? maxCharLimit + EMPTY_EDITOR_CHAR_COUNT : maxCharLimit;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: false,
        bulletList: true,
        codeBlock: false,
        document: true,
        hardBreak: true,
        heading: false,
        horizontalRule: false,
        listItem: true,
        orderedList: true,
        paragraph: true,
        text: true,
        bold: true,
        code: false,
        italic: true,
        strike: true,
        dropcursor: false,
        gapcursor: false,
        history: true,
      }),
      Underline,
      Link.configure({
        HTMLAttributes: {
          class: 'rte-link',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      CharacterCount.configure({
        // have set the limit to null but handling it manually
        // using this extension just to get character count
        limit: null,
        mode: 'nodeSize',
      }),
    ],
    autofocus: false,
    editable: !(disabled || readOnly),
    onUpdate({ editor }) {
      if (editor.storage.characterCount.characters() > maxCharLimit) {
        setCharLimitError(true);
      } else {
        setCharLimitError(false);
      }
    },
    onCreate({ editor }) {
      if (editor.storage.characterCount.characters() > maxCharLimit) {
        setCharLimitError(true);
      } else {
        setCharLimitError(false);
      }
    },
    onFocus({ editor, event }) {
      setIsFocused(true);
    },
    content: initialText || plainText,
  });

  const [isFocused, setIsFocused] = useState(false);
  const editorContainerRef = useRef();

  const onClickOutside = useCallback(() => {
    if (isFocused) {
      setIsFocused(false);

      if (!charLimitError) {
        const htmlText = sanitizeHtml(editor.getHTML());
        const plainText = editor.getText();

        onBlur(htmlText, plainText);
      }
    }
  }, [editor, isFocused, onBlur, charLimitError]);

  useOnClickOutside(editorContainerRef, onClickOutside);

  if (!editor) {
    return null;
  }

  return (
    <>
      <div
        ref={editorContainerRef}
        className={`editor-container ${(showError || charLimitError) && !readOnly ? 'error' : ''} ${
          readOnly ? 'read-only' : ''
        }`}
        style={{
          width: containerWidth,
        }}
        onClick={() => setIsFocused(true)}
      >
        {showToolbar ? <RTEMenuBar editor={editor} /> : null}
        <EditorContent editor={editor} />
        {!readOnly ? (
          <Box className="character-count" padding="0 5px" display="flex" justifyContent="space-between">
            <Typography12 align="left">{loading ? <CircularProgress size={15} /> : ''}</Typography12>
            <Typography12 align="right" color={charLimitError ? theme.palette.text.red : theme.palette.grey.dark}>
              {editor.storage.characterCount.characters() - EMPTY_EDITOR_CHAR_COUNT}/
              {maxCharLimit - EMPTY_EDITOR_CHAR_COUNT}
            </Typography12>
          </Box>
        ) : null}
      </div>
      {charLimitError && !readOnly ? (
        <Box>
          <Typography12 color={theme.palette.text.red} align="left">
            Character limit exceeded, your changes will not be saved
          </Typography12>
        </Box>
      ) : null}
    </>
  );
}

export default RichTextEditor;
